import { Container, Point } from 'pixi.js';
import { Signal } from 'typed-signals';
import { getLocalizedText, getLocalizedValue } from '@softgames/localization';
import type { MenuPage } from '../MainMenuView';
import { MainPageLayout } from '../../../layouts/MainPageLayout';
import { getLocalizedString } from '../../app/localization/getLocalizedString';
import type { AppStateController } from '../../app/state/AppStateController';
import { setContainerParent } from '../../utils/pixi/transform-utils';
import { waitForNextFrame } from '../../utils/async-utils';
import { DioramaView } from '../../game/views/dioramas/DioramaView';
import { dioramaConfigs } from '../../app/state/diorama/DioramaConfig';
import { DioramaComingSoonView } from '../../game/views/dioramas/DioramaComingSoonView';
import type { DioramaDisplayViewConfig } from './DioramaDisplayView';
import { DioramaDisplayView } from './DioramaDisplayView';
import { DioramaProgressBarView } from '../../game/views/dioramas/DioramaProgressBarView';
import type { AllProps } from '../../libs/propsMixin/props';
import type { MenuIcon } from '../types';
import { HomePageIconPositionHandler } from './HomePageIconPositionHandler';
import { getBannerHeight, getSafeAreaInset } from '../../safearea/SafeAreaProvider';
import type { CollectionEventProgressBarView } from '../../gameEvents/collectionEvent/views/CollectionEventProgressBarView';

type HomePageConfig = {
  state: AppStateController;
  nextLevelNumber: number;
  dioramaView?: DioramaView;
  onDioramaAnimationFinished: () => void;
  collectionEventProgressView: CollectionEventProgressBarView | null;
};

export class HomePageView extends Container implements MenuPage {
  public readonly onActionButtonClicked = new Signal();

  public readonly onDioramaClicked = new Signal();

  private readonly layout = new MainPageLayout();

  private dioramaView: DioramaView | null = null;

  private dioramaPlaceholder: Container | null = null;

  private dioramaDisplay: DioramaDisplayView | null = null;

  private readonly dioramaProgressBarView: DioramaProgressBarView;

  private readonly eventIconHandler = new HomePageIconPositionHandler(this.layout.eventIconsSlot);

  private readonly resourceIconHandler = new HomePageIconPositionHandler(this.layout.resourceIconsSlot);

  constructor(private readonly config: HomePageConfig) {
    super();

    this.dioramaProgressBarView = new DioramaProgressBarView(
      this.layout,
      this.config.state.getCurrentDioramaState(),
      false,
    );

    this.init(config);
  }

  public isAboveTopBar(): boolean {
    return false;
  }

  private init(config: HomePageConfig): void {
    const { root } = this.layout;

    this.addChild(root);

    void this.initDiorama(config);

    this.initActionButton(config);

    this.initDioramaButton(config);

    this.initCollectionEventProgressBar(config);
  }

  private initCollectionEventProgressBar(config: HomePageConfig): void {
    const { collectionEventProgressView } = config;

    collectionEventProgressView?.props({
      parent: this.layout.collectionEventSlot,
    });

    collectionEventProgressView?.addVisibilityChangeListener((visible) => {
      this.layout.collectionEventSlot.props({
        visible,
      });
    });
  }

  private initActionButton({ state, nextLevelNumber }: HomePageConfig): void {
    const {
      playButtonLevelText,
      playButton: { root: playButton },
      outOfLivesButton,
    } = this.layout;

    playButtonLevelText.text = getLocalizedString(`!{Level} ${getLocalizedValue(nextLevelNumber)}`);

    playButton.on('pointertap', () => {
      this.onActionButtonClicked.emit();
    });

    outOfLivesButton.onClicked.connect(() => {
      this.onActionButtonClicked.emit();
    });
    playButton.visible = false;

    state.subscriptions.onChange(
      ['lives'],
      () => {
        this.refreshActionButton(state.getLives());
      },
      this,
    );
    this.refreshActionButton(state.getLives());

    this.once('destroyed', () => {
      state.subscriptions.removeAllSubscriptions(this);
    });
  }

  private refreshActionButton(lives: number): void {
    const {
      playButton: { root: playButton },
      outOfLivesButton,
    } = this.layout;
    playButton.visible = lives > 0;
    outOfLivesButton.visible = !playButton.visible;
  }

  private initDioramaButton(config: HomePageConfig): void {
    const {
      dioramaButtonText,
      dioramaButton: { root: dioramaButton },
    } = this.layout;

    const dioramaState = config.state.getCurrentDioramaState();

    dioramaButtonText.text = getLocalizedText('Island <nr>').replace('<nr>', `${dioramaState.dioramaIndex + 1}`);

    dioramaButton.on('pointertap', () => {
      this.onDioramaClicked.emit();
    });

    this.dioramaProgressBarView.setProgress(dioramaState.dioramaIndex);
  }

  private async initDiorama(config: HomePageConfig): Promise<void> {
    const { dioramaView, state, onDioramaAnimationFinished } = config;
    const { dioramaSlot } = this.layout;

    dioramaSlot.on('pointertap', () => {
      this.onDioramaClicked.emit();
    });

    if (!dioramaView) {
      const { dioramaIndex, tilesAmount } = state.getCurrentDioramaState();
      const dioramaConfig = dioramaConfigs[dioramaIndex];
      const totalTileAmount = dioramaConfig.pieces.reduce((a, { price }) => a + price, 0);
      const dioramaIsComplete = tilesAmount >= totalTileAmount;

      if (!dioramaIsComplete) {
        this.dioramaView = new DioramaView({
          config: dioramaConfig,
          progress: tilesAmount,
          isLocked: false,
          hideBackground: true,
        }).props({
          parent: dioramaSlot,
        });
      } else {
        this.dioramaPlaceholder = new DioramaComingSoonView().props({
          parent: dioramaSlot,
        });
      }

      onDioramaAnimationFinished();
      return;
    }

    // wait for transforms being updated
    await waitForNextFrame();
    const target = dioramaSlot.getGlobalPosition();

    // interactivity should be locked until this is complete, or it can cause accidental clicks / buggy visuals.
    this.interactiveChildren = false;

    await dioramaView
      .tweenTo(
        {
          scaleX: 1,
          scaleY: 1,
          x: target.x,
          y: target.y,
        },
        1000,
        'sinOut',
        true,
      )
      .asyncOnComplete();

    this.dioramaView = dioramaView;

    if (this.destroyed) {
      this.dioramaView.destroy();
      return;
    }

    this.dioramaView.interactiveChildren = true;
    this.interactiveChildren = true;
    setContainerParent(this.dioramaView, this.layout.dioramaSlot);
    onDioramaAnimationFinished();
  }

  public show(): void {}

  public hide(): void {}

  public resize(width: number, height: number): void {
    const {
      layout: {
        buttonContainer,
        leaderboardSlot,
        dioramaContainer,
        dioramaSlot,
        collectionEventSlot,
        eventIconsSlot,
        resourceIconsSlot,
      },
      config: { state },
    } = this;
    const isHorizontal = state.isHorizontalLayoutActive();
    const safeArea = getSafeAreaInset();
    const bannerHeight = getBannerHeight();

    dioramaContainer.props({
      ...this.getDioramaContainerDisplayProps(),
      y: isHorizontal ? 460 + safeArea.top : Math.floor(360 + (height - 900) * 0.5) + safeArea.top,
    });

    eventIconsSlot.props({
      x: width * -0.5 + 95 + safeArea.left,
      y: 88 + safeArea.top,
    });

    resourceIconsSlot.props({
      x: width * 0.5 - 80 - safeArea.right,
      y: 88 + safeArea.top,
    });

    dioramaSlot.scale.set(isHorizontal ? 1.26 : 1);
    buttonContainer.y = height - 60 - safeArea.bottom - bannerHeight;
    collectionEventSlot.y = height - 220 - safeArea.bottom - bannerHeight;
    leaderboardSlot.y = height - 260 - safeArea.bottom - bannerHeight;
    this.dioramaDisplay?.props?.({
      position: this.toLocal(new Point(0, 0)),
    });
  }

  private getDioramaContainerDisplayProps(): AllProps {
    const {
      config: { state },
      dioramaDisplay,
    } = this;

    if (state.isHorizontalLayoutActive()) {
      return {
        scaleX: 1,
        scaleY: 1,
        pivotY: 0,
      };
    }

    return {
      scaleX: dioramaDisplay ? 1.2 : 1,
      scaleY: dioramaDisplay ? 1.2 : 1,
      pivotY: dioramaDisplay ? -100 : 0,
    };
  }

  public openDioramaDisplay(
    viewConfig: Omit<DioramaDisplayViewConfig, 'dioramaSlot' | 'initDiorama'>,
  ): DioramaDisplayView | null {
    if (this.dioramaDisplay) return null;

    console.warn('open diorama display');

    const { homePageUI } = this.layout;

    this.dioramaDisplay = new DioramaDisplayView({
      ...viewConfig,
    }).props({
      parent: this,
      position: this.toLocal(new Point(0, 0)),
    });

    this.dioramaPlaceholder?.tweenTo({ alpha: 0 }, 300, 'sinIn', true);

    homePageUI.interactiveChildren = false;

    return this.dioramaDisplay;
  }

  public async closeDioramaDisplay(): Promise<void> {
    const { dioramaDisplay } = this;

    if (!dioramaDisplay) return;
    const { homePageUI, dioramaContainer } = this.layout;
    this.dioramaDisplay = null;
    void dioramaDisplay.closeAndDestroy();

    this.dioramaPlaceholder?.tweenTo({ alpha: 1 }, 300, 'sinOut', true);

    dioramaContainer.tweenTo(this.getDioramaContainerDisplayProps(), 300, 'sinOut', true);

    await homePageUI.tweenTo({ alpha: 1 }, 300, 'sinOut', true).asyncOnComplete();
    homePageUI.interactiveChildren = true;
  }

  public addEventIcon(icon: MenuIcon | null): void {
    if (!icon) return;
    this.eventIconHandler.addIcon(icon);
  }

  public addResourceIcon(icon: MenuIcon | null): void {
    if (!icon) return;
    this.resourceIconHandler.addIcon(icon);
  }

  public getPlayButton(): Container {
    return this.layout.playButton.root;
  }
}
